import { Link } from "react-router-dom";
import { TodaySection } from "./today-section";
import { EmptyState } from "@/components/v2/empty-state";
import { useTodaysJobs } from "@/hooks/use-api";

// ADV-799: Today's Jobs section. CASL is applied at the API (/ops/jobs); this
// component just renders the response. Per-role data scope is upstream — UI
// reads the same hook for every variant.

interface JobRow {
  id: string;
  job_number?: string | null;
  status?: string | null;
  scheduled_date?: string | null;
  customer_name?: string | null;
  assigned_rep_name?: string | null;
}

interface JobsResponse {
  jobs?: JobRow[];
  total?: number;
}

function SkeletonRows({ count = 3 }: { count?: number }) {
  return (
    <ul className="space-y-2">
      {Array.from({ length: count }).map((_, i) => (
        <li
          key={i}
          className="h-9 animate-pulse rounded-md bg-[color:var(--color-surface-2)]"
        />
      ))}
    </ul>
  );
}

interface SectionProps {
  /** Optional title override applied by Phase 5 role variants. */
  title?: string;
}

export function TodaysJobsSection({
  title = "Today's jobs",
}: SectionProps = {}) {
  const { data, isLoading, isError } = useTodaysJobs();
  const response = data as JobsResponse | undefined;
  const jobs = response?.jobs ?? [];

  return (
    <TodaySection
      id="today-section-jobs"
      title={title}
      count={response ? jobs.length : undefined}
    >
      {isLoading ? (
        <SkeletonRows count={3} />
      ) : isError ? (
        <EmptyState
          title="Couldn't load jobs"
          description="Try refreshing the page."
        />
      ) : jobs.length === 0 ? (
        <EmptyState
          title="No jobs today"
          description="Nothing scheduled on the calendar."
        />
      ) : (
        <ul className="divide-y divide-[color:var(--color-border)]">
          {jobs.map((j) => (
            <li
              key={j.id}
              className="flex items-center justify-between py-2 text-sm"
            >
              <Link
                to={`/jobs/${j.id}`}
                className="font-medium text-[color:var(--color-text-hi)] hover:underline"
              >
                {j.job_number ?? `Job ${j.id.slice(-6).toUpperCase()}`}
              </Link>
              <span className="text-[color:var(--color-text-md)]">
                {j.customer_name ?? "—"}
              </span>
              <span className="tabular-nums text-[color:var(--color-text-md)]">
                {j.status ?? ""}
              </span>
            </li>
          ))}
        </ul>
      )}
    </TodaySection>
  );
}
